import { cn } from '@/utils/cn';

interface TabsProps<T extends string> {
  tabs: readonly T[];
  active: T;
  onChange: (tab: T) => void;
  /** Layout for the bar itself — the master screens pin it to the top of a scrolling form. */
  className?: string;
}

/**
 * The tab strip along the top of a master form.
 *
 * Seven of the eight master screens carried this markup byte for byte, and the eighth had drifted
 * into a different container. Repeated markup is where inconsistency starts: the first person to
 * restyle a tab restyles one screen.
 *
 * Generic over the tab union so `active` and `onChange` keep the page's own `type Tab` rather than
 * widening to string — a typo in a tab name stays a compile error.
 */
export const Tabs = <T extends string>({ tabs, active, onChange, className }: TabsProps<T>) => (
  <div
    role="tablist"
    className={cn(
      'flex border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800',
      'px-5 sticky top-0 z-10',
      className,
    )}
  >
    {tabs.map(tab => (
      <button
        key={tab}
        type="button"
        role="tab"
        aria-selected={active === tab}
        onClick={() => onChange(tab)}
        className={cn(
          'px-4 py-2.5 text-xs font-medium border-b-2 transition-colors whitespace-nowrap',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-inset',
          active === tab
            ? 'border-brand-500 text-brand-600 dark:text-brand-400'
            : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200',
        )}
      >
        {tab}
      </button>
    ))}
  </div>
);
